<script lang="ts" setup>
import { computed } from 'vue';

import { StaffStore } from '@/services/StaffStore.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { useDataSource } from '@nzyme/vue-utils/useDataSource.js';
import type { ChatStaffMessage } from '@sigma/chat-common/ChatEvent.js';
import { defineChatEventProps } from '@sigma/client/chat/defineChatEventProps.js';
import Avatar from '@sigma/ui/Avatar.vue';
import ChatEventWrapper from '@sigma/ui/chat/ChatEventWrapper.vue';
import ChatFiles from '@sigma/ui/chat/ChatFiles.vue';
import ChatMessage from '@sigma/ui/chat/ChatMessage.vue';

import chatCss from './chat.module.scss';

const props = defineProps(defineChatEventProps<ChatStaffMessage>());
const staffService = useService(StaffStore);
const staff = useDataSource({
  params: () => props.event.staffId,
  load: staffId => staffService.get(staffId),
  behavior: 'eager',
});

const isFirst = computed(() => {
  const previousEvent = props.chat.events[props.event.seq - 1];
  return !previousEvent || previousEvent.type !== 'STAFF_MESSAGE';
});

const isLast = computed(() => {
  const nextEvent = props.chat.events[props.event.seq + 1];
  return !nextEvent || nextEvent.type !== 'STAFF_MESSAGE';
});
</script>

<template>
  <ChatEventWrapper
    tid="staff-message"
    :event="event"
    :class="chatCss.container"
  >
    <ChatMessage
      color="white"
      align="left"
      :is-first="isFirst"
      :is-last="isLast"
      :timestamp="event.timestamp"
    >
      <template #avatar>
        <Avatar
          color="blue"
          :first-name="staff.value?.firstName"
          :last-name="staff.value?.lastName"
          :loading="!staff.value"
        />
      </template>

      <template
        v-if="event.files?.length"
        #top
      >
        <ChatFiles
          :files="event.files"
          color="white"
        />
      </template>

      <template #content>
        {{ event.message }}
      </template>
    </ChatMessage>
  </ChatEventWrapper>
</template>
